<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="close"></div>

      <!-- Modal content -->
      <div class="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:text-right w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                انتخاب تاریخ
              </h3>
              
              <!-- Calendar Header -->
              <div class="flex items-center justify-between mb-4">
                <button 
                  @click="previousMonth"
                  class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>
                <div class="text-lg font-semibold text-gray-900">
                  {{ currentMonthName }} {{ currentYear }}
                </div>
                <button 
                  @click="nextMonth"
                  class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>

              <!-- Week days header -->
              <div class="grid grid-cols-7 gap-1 mb-2">
                <div class="text-center text-sm font-medium text-gray-500 p-2">ش</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">ی</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">د</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">س</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">چ</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">پ</div>
                <div class="text-center text-sm font-medium text-gray-500 p-2">ج</div>
              </div>

              <!-- Calendar days -->
              <div class="grid grid-cols-7 gap-1">
                <button
                  v-for="day in calendarDays"
                  :key="day.key"
                  @click="selectDate(day)"
                  :disabled="!day.isCurrentMonth"
                  :class="[
                    'p-2 text-sm rounded-lg transition-colors',
                    day.isCurrentMonth 
                      ? 'hover:bg-blue-100 cursor-pointer' 
                      : 'text-gray-300 cursor-not-allowed',
                    day.isSelected 
                      ? 'bg-brown-600 text-white hover:bg-brown-700' 
                      : day.isCurrentMonth 
                        ? 'text-gray-900' 
                        : 'text-gray-300'
                  ]"
                >
                  {{ day.day }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Modal footer -->
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            @click="confirmSelection"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brown-600 text-base font-medium text-white hover:bg-brown-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            تایید
          </button>
          <button
            @click="close"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            انصراف
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import moment from 'moment-jalaali'

export default {
  name: 'PersianDatePicker',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { emit }) {
    const currentDate = ref(moment())
    const selectedDate = ref(null)

    // Initialize selected date from model value
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        const parsed = moment(newValue, 'jYYYY/jMM/jDD')
        if (parsed.isValid()) {
          selectedDate.value = parsed
          currentDate.value = parsed.clone()
        }
      }
    }, { immediate: true })

    const currentYear = computed(() => currentDate.value.jYear())
    const currentMonth = computed(() => currentDate.value.jMonth())
    const currentMonthName = computed(() => {
      const monthNames = [
        'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
        'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
      ]
      return monthNames[currentMonth.value]
    })

    const calendarDays = computed(() => {
      const days = []
      
      // Get the number of days in the current Persian month
      const getDaysInMonth = (year, month) => {
        const monthDays = [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 30, 30]
        if (month === 11 && year % 4 === 3) return 30 // Leap year in Persian calendar
        return monthDays[month]
      }
      
      const daysInMonth = getDaysInMonth(currentYear.value, currentMonth.value)
      const firstDayOfMonth = moment(`${currentYear.value}/${currentMonth.value + 1}/1`, 'jYYYY/jM/jD')
      
      // Get the day of week for the first day (0 = Saturday, 1 = Sunday, etc.)
      const firstDayWeekday = firstDayOfMonth.day()
      
      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDayWeekday; i++) {
        days.push({
          day: '',
          isCurrentMonth: false,
          isSelected: false,
          key: `empty-${i}`
        })
      }
      
      // Add all days of the current month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = moment(`${currentYear.value}/${currentMonth.value + 1}/${day}`, 'jYYYY/jM/jD')
        const isSelected = selectedDate.value && 
          selectedDate.value.jYear() === date.jYear() &&
          selectedDate.value.jMonth() === date.jMonth() &&
          selectedDate.value.jDate() === date.jDate()
        
        days.push({
          day: day,
          date: date,
          isCurrentMonth: true,
          isSelected: isSelected,
          key: `day-${day}`
        })
      }
      
      return days
    })

    const previousMonth = () => {
      currentDate.value = currentDate.value.subtract(1, 'jMonth')
    }

    const nextMonth = () => {
      currentDate.value = currentDate.value.add(1, 'jMonth')
    }

    const selectDate = (day) => {
      if (day.isCurrentMonth && day.date) {
        selectedDate.value = day.date.clone()
      }
    }

    const confirmSelection = () => {
      if (selectedDate.value) {
        const formattedDate = selectedDate.value.format('jYYYY/jMM/jDD')
        emit('update:modelValue', formattedDate)
      }
      close()
    }

    const close = () => {
      emit('close')
    }

    return {
      currentYear,
      currentMonth,
      currentMonthName,
      calendarDays,
      selectedDate,
      previousMonth,
      nextMonth,
      selectDate,
      confirmSelection,
      close
    }
  }
}
</script>

<style scoped>
/* Custom styles for Persian date picker */
.grid {
  direction: rtl;
}
</style>
